"use client";

import { useState, useEffect } from "react";
import styles from "../css/DeliveryModal.module.css";
import { User, MapPin, Clock, Pill, Banknote, CreditCard } from "lucide-react";
import PriorityPill from "./PriorityPill";
import { fetchDeliveryDrivers } from "../../../../../api/dispensary/Delivery/deliveryApi";

const DeliveryModal = ({ isOpen, onClose, deliveryData, selectedDeliveries = [], onAssign, onSelectAddress }) => {
  const [applySameToAll, setApplySameToAll] = useState(false);
  const [driverAssignments, setDriverAssignments] = useState({});
  const [addressSelections, setAddressSelections] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverError, setDriverError] = useState("");

  // Disable body scroll when modal opens; re‑enable on close/unmount
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  // Initialize driverAssignments when deliveries change
  useEffect(() => {
    const list = selectedDeliveries && selectedDeliveries.length > 0
      ? selectedDeliveries
      : (deliveryData ? [deliveryData] : []);
    const initial = list.reduce((acc, d) => {
      acc[d.orderId] = "";
      return acc;
    }, {});
    setDriverAssignments(initial);

    const initialAddresses = list.reduce((acc, d) => {
      const idx = typeof d.selectedDeliveryAddressIndex === 'number' ? d.selectedDeliveryAddressIndex : 0;
      acc[d.orderId] = idx;
      return acc;
    }, {});
    setAddressSelections(initialAddresses);
  }, [selectedDeliveries, deliveryData]);

  const getSelectedAddressForDelivery = (d) => {
    const all = Array.isArray(d?.deliveryAddresses) ? d.deliveryAddresses : [];
    const idx = typeof addressSelections[d.orderId] === 'number' ? addressSelections[d.orderId] : 0;
    if (all.length && all[idx]) return all[idx];
    return d.address || '';
  };

  const handleAddressChange = (orderId, nextIndex) => {
    setAddressSelections((prev) => ({ ...prev, [orderId]: nextIndex }));
    if (typeof onSelectAddress === 'function') {
      onSelectAddress(orderId, nextIndex);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    const loadDrivers = async () => {
      try {
        setLoadingDrivers(true);
        setDriverError("");
        const list = await fetchDeliveryDrivers();
        if (!active) return;
        setDrivers(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        setDriverError("Failed to load drivers");
      } finally {
        if (active) setLoadingDrivers(false);
      }
    };

    loadDrivers();

    return () => {
      active = false;
    };
  }, [isOpen]);

  const handleDriverChange = (orderId, driverId) => {
    setDriverAssignments((prev) => ({ ...prev, [orderId]: driverId }));
    if (applySameToAll) {
      const list = selectedDeliveries && selectedDeliveries.length > 0
        ? selectedDeliveries
        : (deliveryData ? [deliveryData] : []);
      const uniform = list.reduce((acc, d) => {
        acc[d.orderId] = driverId;
        return acc;
      }, {});
      setDriverAssignments(uniform);
    }
  };

  const handleConfirm = () => {
    const list = selectedDeliveries && selectedDeliveries.length > 0
      ? selectedDeliveries
      : (deliveryData ? [deliveryData] : []);

    const listWithSelectedAddresses = list.map((d) => {
      const idx = typeof addressSelections[d.orderId] === 'number' ? addressSelections[d.orderId] : 0;
      const selected = Array.isArray(d?.deliveryAddresses) ? d.deliveryAddresses[idx] : '';
      const address = selected || d.address || '';
      return {
        ...d,
        address,
        selectedDeliveryAddressIndex: idx,
      };
    });

    console.log('🔍 [DELIVERY MODAL] handleConfirm called:', {
      listLength: list.length,
      applySameToAll,
      driverAssignments,
      list
    });

    // If applySameToAll, send a single assignment; otherwise send per-delivery assignments
    if (applySameToAll) {
      const firstDriverId = Object.values(driverAssignments).find(v => v);
      const driver = drivers.find((d) => d.id === firstDriverId);
      console.log('🔍 [DELIVERY MODAL] applySameToAll path:', { firstDriverId, driver });
      if (driver) {
        const payload = listWithSelectedAddresses.length > 1 ? listWithSelectedAddresses : listWithSelectedAddresses[0];
        console.log('🔍 [DELIVERY MODAL] calling onAssign with:', { driver, payload });
        onAssign(driver, payload);
      }
    } else {
      // Send each assignment individually
      console.log('🔍 [DELIVERY MODEL] individual assignments path');
      Object.entries(driverAssignments).forEach(([orderId, driverId]) => {
        const driver = drivers.find((d) => d.id === driverId);
        const delivery = listWithSelectedAddresses.find((d) => String(d.orderId) === String(orderId));
        console.log('🔍 [DELIVERY MODAL] checking assignment:', {
          orderId,
          driverId,
          driver: !!driver,
          delivery: !!delivery,
          driverName: driver?.name,
          deliveryOrderId: delivery?.orderId,
          orderIdType: typeof orderId,
          deliveryOrderIdType: typeof delivery?.orderId,
          comparison: delivery ? String(delivery.orderId) === String(orderId) : 'N/A'
        });
        if (driver && delivery) {
          console.log('🔍 [DELIVERY MODAL] calling onAssign individually:', { driver, delivery });
          onAssign(driver, delivery);
        } else {
          console.log('❌ [DELIVERY MODAL] skipping assignment - missing driver or delivery');
        }
      });
    }
    onClose();
  };

  const isConfirmDisabled = applySameToAll
    ? !Object.values(driverAssignments).some(v => v)
    : Object.values(driverAssignments).some(v => !v);

  if (!isOpen) return null;

  const list = selectedDeliveries && selectedDeliveries.length > 0
    ? selectedDeliveries
    : (deliveryData ? [deliveryData] : []);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>Assign Driver</h2>
            <p className={styles.modalSubtitle}>
              {list.length > 1
                ? `You have selected ${list.length} deliveries`
                : "Select a driver for this delivery"}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.headerDivider} />

        {list.length > 1 && (
          <div className={styles.sameToAllRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={applySameToAll}
                onChange={(e) => setApplySameToAll(e.target.checked)}
                className={styles.checkboxInput}
              />
              Apply same driver to all deliveries
            </label>
          </div>
        )}

        <div className={styles.orderInfo}>
          <div className={styles.horizontalScrollWrapper}>
            {list.map((d, idx) => (
              <div key={d.orderId} className={styles.deliveryCard}>
                <div className={styles.cardHeaderRow}>
                  <span className={styles.cardIndex}>#{idx + 1}</span>
                  <span className={styles.orderId}>{d.scriptNumber || d.orderId}</span>
                  {d.priority ? <PriorityPill level={d.priority} /> : null}
                </div>
                <div className={styles.orderDetails}>
                  <div className={styles.detailRow}>
                    <User size={18} className={styles.iconSvg} />
                    <span>{d.patient}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <MapPin size={18} className={styles.iconSvg} />
                    <span>{getSelectedAddressForDelivery(d)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <Clock size={18} className={styles.iconSvg} />
                    <span>{d.startTime || d.scheduledTime || ""}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <Pill size={18} className={styles.iconSvg} />
                    <span>{d.items}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <Banknote size={18} className={styles.iconSvg} />
                    <span>{d.amount || ""}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <CreditCard size={18} className={styles.iconSvg} />
                    <span>{d.paymentType || ""}</span>
                  </div>
                </div>

                {Array.isArray(d?.deliveryAddresses) && d.deliveryAddresses.length > 1 && (
                  <div className={styles.driverSelector}>
                    <h4 className={styles.driverLabelTitle}>Delivery Address</h4>
                    <select
                      value={typeof addressSelections[d.orderId] === 'number' ? addressSelections[d.orderId] : 0}
                      onChange={(e) => handleAddressChange(d.orderId, Number(e.target.value))}
                      className={styles.selectInput}
                    >
                      {d.deliveryAddresses.map((addr, i) => (
                        <option key={i} value={i}>
                          {addr}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Driver selector for this delivery */}
                <div className={styles.driverSelector}>
                  <h4 className={styles.driverLabelTitle}>Driver</h4>
                  {loadingDrivers && <div className={styles.driverStatus}>Loading drivers...</div>}
                  {driverError && !loadingDrivers && (
                    <div className={styles.driverStatus}>{driverError}</div>
                  )}
                  {!loadingDrivers && !driverError && drivers.map((driver) => (
                    <label key={driver.id} className={styles.driverOptionLabel}>
                      <input
                        type="radio"
                        name={`driver-${d.orderId}`}
                        value={driver.id}
                        checked={driverAssignments[d.orderId] === driver.id}
                        onChange={() => handleDriverChange(d.orderId, driver.id)}
                        className={styles.radioInput}
                      />
                      <span className={styles.driverName}>{driver.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className={styles.confirmButton}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

export default DeliveryModal;